/**
 * Shared (platform-agnostic) logic for the building state of an artifact the
 * streaming writer is making (plan 3.4, 5.3 and 7.2): reading the
 * `artifact-draft` SSE event, what the in-chat card and the canvas panel show
 * at each phase, the mini views' lines and outline rows, and the small HTML
 * tokenizer the code views colour from. Rendering stays in the platform
 * files; everything here is data, so web and native cannot drift.
 */
import type {
  ArtifactDraftEvent,
  ArtifactDraftKind,
  ArtifactDraftMode,
  ArtifactDraftOutlineEntry,
  ArtifactDraftPhase,
} from '@tabbio/types';

export const ARTIFACT_DRAFT_PHASES: readonly ArtifactDraftPhase[] = [
  'start',
  'code',
  'outline',
  'snapshot',
  'checking',
  'end',
  'error',
];
const KINDS: readonly ArtifactDraftKind[] = ['page', 'document', 'slides'];
const MODES: readonly ArtifactDraftMode[] = ['create', 'edit'];

/** Documents carry at most 12 sections, so an outline never holds more. */
export const ARTIFACT_DRAFT_OUTLINE_MAX = 12;

export type ArtifactDraftStats = NonNullable<ArtifactDraftEvent['stats']>;

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function readOutline(value: unknown): ArtifactDraftOutlineEntry[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const entries: ArtifactDraftOutlineEntry[] = [];
  for (const item of value) {
    const entry = record(item);
    if (!entry || typeof entry.heading !== 'string') continue;
    entries.push({ heading: entry.heading, done: entry.done === true });
    if (entries.length === ARTIFACT_DRAFT_OUTLINE_MAX) break;
  }
  return entries;
}

/**
 * The payload of an `artifact-draft` SSE event, or null when it is not one a
 * client can use (no call id, an unknown phase or kind, no sequence number).
 * Optional fields are kept only when they have the documented shape.
 */
export function readArtifactDraftEvent(value: unknown): ArtifactDraftEvent | null {
  const source = record(value);
  if (!source) return null;
  const { toolCallId, artifactId, seq, phase } = source;
  if (typeof toolCallId !== 'string' || !toolCallId) return null;
  if (typeof artifactId !== 'string') return null;
  if (typeof seq !== 'number' || !Number.isInteger(seq) || seq < 1) return null;
  if (!(ARTIFACT_DRAFT_PHASES as readonly unknown[]).includes(phase)) return null;
  if (!(KINDS as readonly unknown[]).includes(source.kind)) return null;
  const event: ArtifactDraftEvent = {
    toolCallId,
    artifactId,
    kind: source.kind as ArtifactDraftKind,
    mode: (MODES as readonly unknown[]).includes(source.mode) ? (source.mode as ArtifactDraftMode) : 'create',
    title: typeof source.title === 'string' ? source.title : '',
    seq,
    phase: phase as ArtifactDraftPhase,
  };
  if (typeof source.text === 'string') event.text = source.text;
  const outline = readOutline(source.outline);
  if (outline) event.outline = outline;
  if (typeof source.html === 'string') event.html = source.html;
  const stats = record(source.stats);
  if (stats) event.stats = { chars: count(stats.chars), lines: count(stats.lines), sections: count(stats.sections) };
  if (typeof source.versionNumber === 'number') event.versionNumber = source.versionNumber;
  const error = record(source.error);
  if (error) {
    event.error = {
      code: typeof error.code === 'string' && error.code ? error.code : 'WRITER_FAILED',
      message: typeof error.message === 'string' ? error.message : '',
    };
  }
  return event;
}

// ── The draft a card or the panel reads ────────────────────────────────────

/** One build as the draft store holds it (plan 7.1). */
export type ArtifactDraft = {
  toolCallId: string;
  artifactId: string;
  kind: ArtifactDraftKind;
  mode: ArtifactDraftMode;
  title: string;
  /**
   * The lifecycle phase: `snapshot` never takes the place of `checking`, so
   * the final snapshot sent after the checks does not bring "Building" back.
   */
  phase: ArtifactDraftPhase;
  /** The highest sequence number applied; older and repeated events are ignored. */
  seq: number;
  /** Pages: the source so far, capped at 600 KB (the rest is dropped). */
  code: string;
  codeTruncated: boolean;
  /** Documents and slides: the headings so far, the last one possibly half-written. */
  outline: ArtifactDraftOutlineEntry[];
  /** The newest sanitized snapshot in the server's shell. */
  snapshotHtml: string | null;
  /** How many snapshots arrived, so a frame can crossfade to each new one. */
  snapshotCount: number;
  stats: ArtifactDraftStats | null;
  startedAt: number;
  endedAt: number | null;
  versionNumber: number | null;
  error: { code: string; message: string } | null;
  /** The call's tool result arrived on the live stream. */
  resultArrived: boolean;
};

/** Props of both `ArtifactDraftPanel` twins: the canvas panel's body while it builds. */
export type ArtifactDraftPanelProps = {
  draft: ArtifactDraft;
  /** Pages have Preview and Code; documents and slides the Preview alone. */
  view: 'preview' | 'code';
  onViewChange: (view: 'preview' | 'code') => void;
  title: string;
};

/** Code kept per build: generous for a page, bounded for a runaway stream. */
export const ARTIFACT_DRAFT_CODE_MAX = 600 * 1024;

export function isDraftTerminal(draft: Pick<ArtifactDraft, 'phase'> | null | undefined): boolean {
  return draft?.phase === 'end' || draft?.phase === 'error';
}

/**
 * Applies one event to a build. Returns the same object when the event is
 * ignored: out of order or repeated (`seq` not above the last one), or after
 * the build ended. A build first seen mid-stream starts from that event.
 */
export function reduceArtifactDraft(
  current: ArtifactDraft | undefined,
  event: ArtifactDraftEvent,
  now: number,
): ArtifactDraft | undefined {
  if (current) {
    if (event.seq <= current.seq) return current;
    if (isDraftTerminal(current)) return current;
  }
  const base: ArtifactDraft = current ?? {
    toolCallId: event.toolCallId,
    artifactId: event.artifactId,
    kind: event.kind,
    mode: event.mode,
    title: event.title,
    phase: 'start',
    seq: 0,
    code: '',
    codeTruncated: false,
    outline: [],
    snapshotHtml: null,
    snapshotCount: 0,
    stats: null,
    startedAt: now,
    endedAt: null,
    versionNumber: null,
    error: null,
    resultArrived: false,
  };
  const next: ArtifactDraft = {
    ...base,
    artifactId: event.artifactId || base.artifactId,
    title: event.title || base.title,
    seq: event.seq,
    stats: event.stats ?? base.stats,
  };
  switch (event.phase) {
    case 'start':
      next.phase = 'start';
      break;
    case 'code': {
      next.phase = base.phase === 'checking' ? 'checking' : 'code';
      const text = event.text ?? '';
      if (text && !base.codeTruncated) {
        const room = ARTIFACT_DRAFT_CODE_MAX - base.code.length;
        if (text.length <= room) {
          next.code = base.code + text;
        } else {
          next.code = base.code + text.slice(0, Math.max(room, 0));
          next.codeTruncated = true;
        }
      }
      break;
    }
    case 'outline':
      next.phase = base.phase === 'checking' ? 'checking' : 'outline';
      if (event.outline) next.outline = event.outline.slice(0, ARTIFACT_DRAFT_OUTLINE_MAX);
      break;
    case 'snapshot':
      next.phase = base.phase === 'checking' ? 'checking' : 'snapshot';
      if (typeof event.html === 'string' && event.html) {
        next.snapshotHtml = event.html;
        next.snapshotCount = base.snapshotCount + 1;
      }
      break;
    case 'checking':
      next.phase = 'checking';
      break;
    case 'end':
      next.phase = 'end';
      next.endedAt = now;
      next.versionNumber = typeof event.versionNumber === 'number' ? event.versionNumber : null;
      break;
    case 'error':
      next.phase = 'error';
      next.endedAt = now;
      next.error = event.error ?? { code: 'WRITER_FAILED', message: '' };
      break;
  }
  return next;
}

/** The user stopped the reply: the build will never send its end. */
export function stopArtifactDraft(draft: ArtifactDraft, now: number): ArtifactDraft {
  if (isDraftTerminal(draft)) return draft;
  return { ...draft, phase: 'error', endedAt: now, error: { code: 'CANCELLED', message: '' } };
}

// ── What the card and the panel show ───────────────────────────────────────

export type DraftCopy = { key: string; params?: Record<string, string | number> };

/** The creating tools the writer can stream, and the kind each call makes. */
export function draftKindForCall(toolName: string, args: unknown): ArtifactDraftKind | null {
  if (toolName === 'createPage') return 'page';
  if (toolName !== 'createDocument') return null;
  return record(args)?.kind === 'slides' ? 'slides' : 'document';
}

/** "Building the page", "Writing the document", "Writing the slides", "Editing", "Checking the page". */
export function draftStatusCopy(draft: Pick<ArtifactDraft, 'kind' | 'mode' | 'phase'>): DraftCopy {
  if (draft.mode === 'edit') return { key: 'chat.artifacts.building.editing' };
  if (draft.kind === 'page' && draft.phase === 'checking') {
    return { key: 'chat.artifacts.building.checking' };
  }
  return { key: `chat.artifacts.building.${draft.kind}` };
}

/**
 * Where a document stands: the heading being written (the first one not
 * done) out of the headings known so far. Null before the first heading.
 */
export function sectionProgress(
  outline: readonly ArtifactDraftOutlineEntry[],
): { current: number; total: number } | null {
  if (outline.length === 0) return null;
  const pending = outline.findIndex((entry) => !entry.done);
  return { current: pending === -1 ? outline.length : pending + 1, total: outline.length };
}

/** The count at the end of the card's meta line: "142 lines" or "Section 3 of 6". */
export function draftDetailCopy(
  draft: Pick<ArtifactDraft, 'kind' | 'mode' | 'stats' | 'outline'>,
): DraftCopy | null {
  if (draft.mode === 'edit') return null;
  if (draft.kind === 'page') {
    const lines = draft.stats?.lines ?? 0;
    return lines > 0 ? { key: 'chat.artifacts.building.lines', params: { count: lines } } : null;
  }
  const progress = sectionProgress(draft.outline);
  if (!progress) return null;
  // The writer sends the headings written so far, so the total is known only
  // when headings after the current one are: "Section 3 of 6". Otherwise
  // "Section 3", never a misleading "Section 3 of 3".
  return progress.current < progress.total
    ? { key: 'chat.artifacts.building.section', params: { current: progress.current, total: progress.total } }
    : { key: 'chat.artifacts.building.sectionOnly', params: { current: progress.current } };
}

/** The card's copy for a failure code; the writer's own three have their words. */
export function draftFailureKey(code: string | null | undefined): string {
  switch (code) {
    case 'CANCELLED':
    case 'WRITER_TIMEOUT':
    case 'WRITER_FAILED':
      return `chat.artifacts.failures.${code}`;
    default:
      return 'chat.artifacts.failed';
  }
}

/**
 * What the in-chat card for a creating call is, at one moment:
 * - `building`: the writer streams (spinner, meta with the count, mini view);
 * - `waiting`: the call runs and no draft came (the writer is off, or its
 *   first event is still on the way): the same card, elapsed seconds, no
 *   mini view;
 * - `failed`: the build or the result failed;
 * - `settled`: the Phase 1 canvas card from the tool result.
 */
export type BuildingCardPhase = 'building' | 'waiting' | 'failed' | 'settled';

export function buildingCardPhase(input: {
  draft: Pick<ArtifactDraft, 'phase'> | null;
  /** The tool result parsed into a card model, if it arrived. */
  result: { status: 'pending' | 'ready' | 'failed' } | null;
  /** The call is still running on the live stream. */
  running: boolean;
  /** The call is one the writer streams (createPage, createDocument). */
  streamable: boolean;
}): BuildingCardPhase {
  const { draft, result, running, streamable } = input;
  if (draft?.phase === 'error' || result?.status === 'failed') return 'failed';
  if (result) return 'settled';
  if (draft) return 'building';
  return running && streamable ? 'waiting' : 'settled';
}

/**
 * Where the Open pill of a building card takes the panel: code for a page
 * being written, the preview otherwise (an edit sends no code to watch).
 */
export function draftOpenView(kind: ArtifactDraftKind, mode: ArtifactDraftMode = 'create'): 'code' | 'preview' {
  return kind === 'page' && mode === 'create' ? 'code' : 'preview';
}

/**
 * Whether an open file is being edited by the agent (user test fix,
 * 2026-09-25), and how far: the panel keeps the file on screen, dimmed, with
 * "Editing" (or "Checking the page") until the new version is its head.
 *
 * - an edit draft still streaming: its phase;
 * - an edit draft that ended with version N while the head read is older
 *   than N: still editing (the head is being read again);
 * - an edit call in flight with no draft (an image, the writer off, MCP):
 *   editing until its result, then until the head reaches its version.
 */
export type AgentEditState = 'editing' | 'checking' | null;

export function agentEditState(input: {
  draft: Pick<ArtifactDraft, 'mode' | 'phase' | 'versionNumber'> | null;
  activity: { phase: 'running' | 'saved'; versionNumber: number | null } | null;
  /** The head's number as the panel read it (null before the first read). */
  headNumber: number | null | undefined;
}): AgentEditState {
  const head = input.headNumber ?? 0;
  const { draft, activity } = input;
  if (draft?.mode === 'edit' && draft.phase !== 'error') {
    if (!isDraftTerminal(draft)) return draft.phase === 'checking' ? 'checking' : 'editing';
    if (draft.versionNumber !== null && head < draft.versionNumber) return 'editing';
  }
  if (activity) {
    if (activity.phase === 'running') return 'editing';
    if (activity.versionNumber !== null && head < activity.versionNumber) return 'editing';
  }
  return null;
}

/**
 * The panel shows the building state while a draft is known and the stored
 * artifact is not settled yet (it may still be loading). A failed build shows
 * its failure; a ready artifact shows the Phase 1 preview.
 */
export function panelDraftState(
  draft: Pick<ArtifactDraft, 'phase'> | null,
  artifactStatus: 'pending' | 'ready' | 'failed' | null | undefined,
): 'none' | 'building' | 'failed' {
  if (!draft) return 'none';
  if (artifactStatus === 'ready') return 'none';
  if (draft.phase === 'error') return 'failed';
  if (artifactStatus === 'failed') return 'none';
  return 'building';
}

/** The last `count` lines of the code, the newest last (the mini view shows 5). */
export function lastCodeLines(code: string, count = 5): string[] {
  if (!code) return [];
  const lines = code.replace(/\r\n?/g, '\n').split('\n');
  // A trailing newline starts an empty line: that is where the caret sits.
  return lines.slice(-count);
}

export type OutlineRow = { heading: string; done: boolean; current: boolean };

/**
 * Up to four outline rows around the heading being written, and how many
 * headings are not shown ("+2 more").
 */
export function outlineWindow(
  outline: readonly ArtifactDraftOutlineEntry[],
  size = 4,
): { rows: OutlineRow[]; more: number } {
  const progress = sectionProgress(outline);
  if (!progress) return { rows: [], more: 0 };
  const currentIndex = progress.current - 1;
  const allDone = outline.every((entry) => entry.done);
  const start = Math.max(0, Math.min(currentIndex - (size - 1), outline.length - size));
  const rows = outline.slice(start, start + size).map((entry, offset) => ({
    heading: entry.heading,
    done: entry.done,
    current: !allDone && start + offset === currentIndex,
  }));
  return { rows, more: Math.max(0, outline.length - rows.length) };
}

/** The page skeleton's section blocks: one per section detected so far, at least two. */
export function skeletonSectionCount(stats: ArtifactDraftStats | null | undefined): number {
  return Math.max(2, Math.min(stats?.sections ?? 0, 12));
}

/**
 * The building state's motion (plan 7.2 and 7.8): the card's height settles
 * in 200 ms, snapshots crossfade in 150 ms, the caret blinks and the skeleton
 * shimmers. Under reduced motion every one of them is off.
 */
export const DRAFT_MOTION = { settleMs: 200, crossfadeMs: 150, caretBlinkMs: 530 } as const;

export function draftMotion(reduceMotion: boolean): {
  settleMs: number;
  crossfadeMs: number;
  caretBlinks: boolean;
  shimmer: boolean;
  spinnerTurns: boolean;
} {
  return reduceMotion
    ? { settleMs: 0, crossfadeMs: 0, caretBlinks: false, shimmer: false, spinnerTurns: false }
    : {
        settleMs: DRAFT_MOTION.settleMs,
        crossfadeMs: DRAFT_MOTION.crossfadeMs,
        caretBlinks: true,
        shimmer: true,
        spinnerTurns: true,
      };
}

/** Whole seconds since a moment, for the writer-off card. */
export function elapsedSeconds(since: number, now: number): number {
  return Math.max(0, Math.floor((now - since) / 1000));
}

/**
 * The native snapshot WebView renders an HTML string: it may stay on that
 * document and never navigate anywhere (links inside the page are inert).
 */
export function isAllowedDraftNavigation(url: string): boolean {
  return url === 'about:blank' || url.startsWith('about:blank#') || url === 'about:srcdoc';
}

/**
 * Auto-open (web only, plan 7.2): the panel docks on the first `start` of a
 * reply in the thread on screen, on a wide screen, unless the user closed the
 * panel during this reply.
 */
export function shouldAutoOpenDraft(input: {
  wide: boolean;
  threadOnScreen: boolean;
  alreadyOpenedThisReply: boolean;
  replyStartedAt: number;
  lastUserCloseAt: number | null;
}): boolean {
  if (!input.wide || !input.threadOnScreen || input.alreadyOpenedThisReply) return false;
  return input.lastUserCloseAt === null || input.lastUserCloseAt < input.replyStartedAt;
}

// ── HTML tokenizer for the code views ──────────────────────────────────────

export type HtmlTokenKind = 'text' | 'tag' | 'attr' | 'string' | 'comment' | 'punct';
export type HtmlToken = { kind: HtmlTokenKind; text: string };

/** Where a line starts: in text, inside a tag, a quoted value or a comment. */
export type HtmlLexState = 'text' | 'tag' | 'value' | 'dq' | 'sq' | 'comment';

const NAME = /[A-Za-z0-9_:.-]/;
const TAG_START = /[A-Za-z!/?]/;

function push(tokens: HtmlToken[], kind: HtmlTokenKind, text: string) {
  if (!text) return;
  const last = tokens[tokens.length - 1];
  if (last && last.kind === kind) last.text += text;
  else tokens.push({ kind, text });
}

/**
 * Tokenizes one line of HTML (tags, attribute names, strings, comments),
 * starting in `state` and returning the state the next line starts in. Small
 * on purpose: the code views colour a stream of model output, they do not
 * validate it. CSS in a style element reads as text.
 */
export function tokenizeHtmlLine(
  line: string,
  state: HtmlLexState = 'text',
): { tokens: HtmlToken[]; state: HtmlLexState } {
  const tokens: HtmlToken[] = [];
  let i = 0;
  let mode = state;
  while (i < line.length) {
    if (mode === 'comment') {
      const end = line.indexOf('-->', i);
      if (end === -1) {
        push(tokens, 'comment', line.slice(i));
        i = line.length;
      } else {
        push(tokens, 'comment', line.slice(i, end + 3));
        i = end + 3;
        mode = 'text';
      }
      continue;
    }
    if (mode === 'dq' || mode === 'sq') {
      const quote = mode === 'dq' ? '"' : "'";
      const end = line.indexOf(quote, i);
      if (end === -1) {
        push(tokens, 'string', line.slice(i));
        i = line.length;
      } else {
        push(tokens, 'string', line.slice(i, end + 1));
        i = end + 1;
        mode = 'tag';
      }
      continue;
    }
    const char = line[i]!;
    if (mode === 'text') {
      if (line.startsWith('<!--', i)) {
        mode = 'comment';
        continue;
      }
      if (char === '<' && TAG_START.test(line[i + 1] ?? '')) {
        let j = i + 1;
        if (line[j] === '/' || line[j] === '!' || line[j] === '?') j += 1;
        push(tokens, 'punct', line.slice(i, j));
        let k = j;
        while (k < line.length && NAME.test(line[k]!)) k += 1;
        push(tokens, 'tag', line.slice(j, k));
        i = k;
        mode = 'tag';
        continue;
      }
      const next = line.indexOf('<', i + 1);
      const end = next === -1 ? line.length : next;
      push(tokens, 'text', line.slice(i, end));
      i = end;
      continue;
    }
    // Inside a tag, or right after `=`.
    if (char === '>') {
      push(tokens, 'punct', '>');
      i += 1;
      mode = 'text';
      continue;
    }
    if (char === '/' && line[i + 1] === '>') {
      push(tokens, 'punct', '/>');
      i += 2;
      mode = 'text';
      continue;
    }
    if (char === '"' || char === "'") {
      push(tokens, 'string', char);
      i += 1;
      mode = char === '"' ? 'dq' : 'sq';
      continue;
    }
    if (char === '=') {
      push(tokens, 'punct', '=');
      i += 1;
      mode = 'value';
      continue;
    }
    if (/\s/.test(char)) {
      let k = i;
      while (k < line.length && /\s/.test(line[k]!)) k += 1;
      push(tokens, 'text', line.slice(i, k));
      i = k;
      continue;
    }
    let k = i;
    while (k < line.length && !/[\s>"'=]/.test(line[k]!) && !(line[k] === '/' && line[k + 1] === '>')) k += 1;
    if (k === i) k = i + 1;
    push(tokens, mode === 'value' ? 'string' : 'attr', line.slice(i, k));
    i = k;
    mode = 'tag';
  }
  // An unquoted value ends with its line.
  return { tokens, state: mode === 'value' ? 'tag' : mode };
}

/**
 * Tokenizes a growing source line by line, keeping the lines already done:
 * each update only re-reads the last line (it may still be growing) and what
 * was appended after it. A source that did not grow from the last one is
 * read again from the start.
 */
export class HtmlLineHighlighter {
  private source = '';
  private readonly done: HtmlToken[][] = [];
  private tailStart = 0;
  private tailState: HtmlLexState = 'text';
  private tail: HtmlToken[] = [];

  update(source: string): void {
    if (source === this.source) return;
    // The store only appends, so checking the last line is enough to know
    // the new source continues the old one.
    const grew =
      source.length >= this.source.length &&
      source.startsWith(this.source.slice(this.tailStart), this.tailStart);
    if (!grew) this.reset();
    this.source = source;
    let from = this.tailStart;
    let newline = source.indexOf('\n', from);
    while (newline !== -1) {
      const line = source.slice(from, newline).replace(/\r$/, '');
      const { tokens, state } = tokenizeHtmlLine(line, this.tailState);
      this.done.push(tokens);
      this.tailState = state;
      from = newline + 1;
      newline = source.indexOf('\n', from);
    }
    this.tailStart = from;
    this.tail = tokenizeHtmlLine(source.slice(from), this.tailState).tokens;
  }

  get lineCount(): number {
    return this.source ? this.done.length + 1 : 0;
  }

  line(index: number): HtmlToken[] {
    if (index < this.done.length) return this.done[index]!;
    return index === this.done.length ? this.tail : [];
  }

  /** The last `count` lines, oldest first. */
  lastLines(count: number): HtmlToken[][] {
    const total = this.lineCount;
    const lines: HtmlToken[][] = [];
    for (let index = Math.max(0, total - count); index < total; index += 1) lines.push(this.line(index));
    return lines;
  }

  private reset() {
    this.source = '';
    this.done.length = 0;
    this.tailStart = 0;
    this.tailState = 'text';
    this.tail = [];
  }
}
